import React, { useState, useCallback } from "react";
import { PlxsText, PlxsAlert } from "@plexlogic/plexus-ui";

import UploadDropzone from "./UploadDropzone";
import UppyUploadComponent from "./UppyUploadComponent";
import useUploadStore from "./useUploadStore";
import { toSentenceCase } from "../../utils/stringUtils";
import { shouldUseChunkedUpload } from "../../utils/configUtils";

interface SmartUploadComponentProps {
  entryMethod: "simple" | "standard" | "advanced";
  drawId?: string;
  onUploadComplete?: (metadata: any) => void;
  onUploadError?: (error: string) => void;
  disabled?: boolean;
}

const SmartUploadComponent: React.FC<SmartUploadComponentProps> = ({
  entryMethod,
  drawId,
  onUploadComplete,
  onUploadError,
  disabled = false,
}) => {
  const [uploadMode, setUploadMode] = useState<"dropzone" | "chunked">(
    "dropzone"
  );

  const [chunkedFile, setChunkedFile] = useState<File | null>(null);

  // Subscribe to upload store state  
  const uploadFile = useUploadStore((state: any) => state.uploadFile);
  const isUploading = useUploadStore(
    (state: any) => state.status === "uploading"
  );
  const uploadStatus = useUploadStore((state: any) => state.status);

  const handleFileSelection = useCallback(
    async (file: File) => {
      // Validate draw ID is available
      if (!drawId) {
        const errorMessage = toSentenceCase("draw ID is required for upload");
        if (onUploadError) {
          onUploadError(errorMessage);
        }
        return;
      }

      try {
        // Determine upload method based on file size
        if (shouldUseChunkedUpload(file.size)) {
          // For large files, use chunked upload mode
          setChunkedFile(file);
          setUploadMode("chunked");
        } else {
          // For smaller files, use regular upload through store
          // The store will handle validation internally and set proper error state
          await uploadFile(file, drawId);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : toSentenceCase("upload failed");
        if (onUploadError) {
          onUploadError(errorMessage);
        }
      }
    },
    [drawId, uploadFile, onUploadError]
  );

  // Monitor upload store state changes and trigger completion callback
  const entriesUpload = useUploadStore((state: any) => state.entriesUpload);
  const error = useUploadStore((state: any) => state.error);
  
  React.useEffect(() => {
    if (uploadStatus === "success" && onUploadComplete && entriesUpload) {
      onUploadComplete(entriesUpload);
    } else if (uploadStatus === "error" && onUploadError && error) {
      onUploadError(error);
    }
  }, [uploadStatus, onUploadComplete, onUploadError, entriesUpload, error]);

  const handleUppyUploadComplete = useUploadStore((state: any) => state.handleUppyUploadComplete);
  
  const handleChunkedUploadComplete = useCallback(
    async (uploadResponse: any) => {
      try {
        // Use the store method to handle Uppy upload completion
        await handleUppyUploadComplete(uploadResponse, drawId);

        // The useEffect above will handle calling onUploadComplete when store updates
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : toSentenceCase("failed to process upload");
        if (onUploadError) {
          onUploadError(errorMessage);
        }
      }
    },
    [drawId, onUploadError, handleUppyUploadComplete]
  );

  const handleChunkedUploadError = useCallback(
    (error: string) => {
      if (onUploadError) {
        onUploadError(error);
      }
    },
    [onUploadError]
  );

  const handleCancel = useCallback(() => {
    setUploadMode("dropzone");
    setChunkedFile(null);
  }, []);

  // If no draw ID provided, show error
  if (!drawId) {
    return (
      <PlxsAlert variant="error">
        <PlxsText size="sm">
          {toSentenceCase("draw ID is required for file upload")}.
        </PlxsText>
      </PlxsAlert>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {/* Upload interface based on mode */}
      {uploadMode === "dropzone" ? (
        <UploadDropzone
          entryMethod={entryMethod}
          onFileUpload={handleFileSelection}
          isUploading={isUploading || disabled}
          hasError={false}
          error={undefined}
        />
      ) : uploadMode === "chunked" ? (
        <div style={{ marginTop: "var(--plexus-space-md, 16px)" }}>
          <UppyUploadComponent
            drawId={drawId || ""}
            initialFile={chunkedFile || undefined}
            onUploadComplete={handleChunkedUploadComplete}
            onUploadError={handleChunkedUploadError}
            onCancel={handleCancel}
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SmartUploadComponent;
